import React from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Editable,
  HStack,
  Link,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { t } from "i18next";
import { DisplayKey } from "./DisplayKey";
import { appRoutes } from "../../../../App";
import { compressUUID } from "../../../shared/utils/format-utils";
import { ManagePermissionsModalProps } from "../../../admin/views/integrations/ManagePermissionsModal";
import { ApiClient, ResourceType } from "../../models/ApiClient";
import { useStores } from "../../../shared/stores/RootStore";
import {
  DeleteIcon,
  DotIcon,
  ExternalLinkIcon,
  UnlockIcon,
} from "../../../shared/views/icons";
import { toast } from "../../../shared/views/CustomToaster";

const i18prefix = "account:Integrations";

interface Props {
  apiClient: ApiClient;
  ManagePermissionsModal: React.FunctionComponent<ManagePermissionsModalProps>;
}

const ApiClientItemBase: React.FC<Props> = ({
  apiClient,
  ManagePermissionsModal,
}) => {
  const { accountStore } = useStores();
  const managePermissionsModal = useDisclosure();

  return (
    <Card.Root p={4} key={apiClient.id} variant="outline" alignSelf="start">
      <Stack gap={4}>
        <Editable.Root
          my={-2}
          fontWeight={"semibold"}
          fontSize={"md"}
          placeholder={t(`${i18prefix}.namePlaceholder`)}
          defaultValue={apiClient.name}
          onValueCommit={({ value }) => {
            console.log(value);
            accountStore
              .updateApiClient(apiClient.id, {
                name: value,
                permissions: apiClient.permissions,
                scopes: apiClient.scopes,
              })
              .then(() => {
                toast("success", t(`${i18prefix}.toast.saveSuccess`));
              })
              .catch(() => {
                toast("error", t(`${i18prefix}.toast.saveFailed`));
              });
          }}
        >
          <Editable.Preview />
          <Editable.Input />
        </Editable.Root>

        <HStack>
          <DisplayKey
            label={t(`${i18prefix}.clientId`)}
            value={apiClient.clientId}
          />
        </HStack>

        {!!apiClient.secretKey && (
          <>
            <DisplayKey
              label={t(`${i18prefix}.secretKey`)}
              value={apiClient.secretKey}
            />
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Text fontSize="sm">{t(`${i18prefix}.secretAlert`)}</Text>
            </Alert.Root>
          </>
        )}

        <Stack>
          <HStack fontSize="xs" alignItems="start">
            <Text>{t(`${i18prefix}.scopes.title`)}:</Text>
            <Text as="span" fontFamily="monospace">
              {apiClient.scopes.join(", ") || "-"}
            </Text>
          </HStack>
          <HStack fontSize="xs" alignItems="start">
            <Text>{t(`${i18prefix}.permissions.title`)}:</Text>
            <HStack flexWrap="wrap" separator={<DotIcon />}>
              {apiClient.permissions
                ? apiClient.permissions.split(",").map((permission) => {
                    const [resource, entity] = permission.split("/");
                    const url =
                      resource === ResourceType.Project
                        ? appRoutes.project(entity)
                        : appRoutes.osaWay(entity);

                    return (
                      <Link
                        key={entity}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontFamily="monospace"
                      >
                        {resource}/
                        {resource === ResourceType.Any
                          ? entity
                          : compressUUID(entity)}
                        <ExternalLinkIcon />
                      </Link>
                    );
                  })
                : "-"}
            </HStack>
          </HStack>
        </Stack>

        <HStack>
          <Spacer />

          <ButtonGroup
            size="sm"
            fontSize={"sm"}
            variant={"ghost"}
            colorPalette="primary"
          >
            <Button onClick={managePermissionsModal.onOpen}>
              <UnlockIcon />
              {t(`${i18prefix}.managePermissions`)}
            </Button>
            <Button
              colorPalette="red"
              onClick={() => {
                if (
                  window.confirm(
                    t(`${i18prefix}.confirmDelete`, {
                      name: apiClient.name,
                    })
                  )
                ) {
                  accountStore.deleteApiClient(apiClient.id).then(() => {
                    toast("success", t(`${i18prefix}.toast.deleteSuccess`));
                  });
                }
              }}
            >
              <DeleteIcon /> {t(`${i18prefix}.delete`)}
            </Button>
          </ButtonGroup>
        </HStack>
      </Stack>

      {managePermissionsModal.open && (
        <ManagePermissionsModal
          onClose={managePermissionsModal.onClose}
          apiClient={apiClient}
        />
      )}
    </Card.Root>
  );
};

export const ApiClientItem = observer(ApiClientItemBase);
